import { useState } from 'react';
import {
  MAX_ASKED_QUESTION_LENGTH,
  type OwnAskedQuestion,
  type PublicAskedQuestion,
} from '@floorpoll/shared';
import { ApiError, apiRequest } from '../../lib/api.js';

/**
 * Audience Q&A on the taker's phone (#118) — ask a question, watch the
 * approved ones, upvote them.
 *
 * Its own file rather than more of Join.tsx, which is already the longest page
 * in the app and has one job: the ballot. Nothing here touches a vote.
 */

interface Props {
  code: string;
  deviceToken: string;
  /** Approved questions for this run, in the moderator's order. */
  questions: PublicAskedQuestion[];
  /** This device's own, whatever their state — the only place a rejection shows. */
  myQuestions: OwnAskedQuestion[];
  /** The one on the projector right now, so the phone can mark it. */
  presentedAskedQuestionId: string | null;
  onAsked: (question: OwnAskedQuestion) => void;
  onLiked: (askedQuestionId: string, likes: number) => void;
}

/**
 * What a status means to the person who asked.
 *
 * `rejected` deliberately does not say "rejected". The word describes a
 * moderator's action, and what the asker needs to know is that it will not be
 * put to the presenter — with the reason beside it when there is one.
 */
const STATUS_LABEL: Record<OwnAskedQuestion['status'], string> = {
  pending: 'Waiting for a moderator',
  approved: 'In the queue',
  rejected: 'Not going forward',
};

export function TakerQanda({
  code,
  deviceToken,
  questions,
  myQuestions,
  presentedAskedQuestionId,
  onAsked,
  onLiked,
}: Props) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which questions this phone has already upvoted. Local and unpersisted: the
  // server holds the real lock (one device, one like) and answers 409 to a
  // second attempt, so this only spares the round trip and keeps the button
  // from looking tappable when it is not.
  const [liked, setLiked] = useState<Set<string>>(new Set());

  const trimmed = draft.trim();
  const remaining = MAX_ASKED_QUESTION_LENGTH - draft.length;

  async function ask(submitEvent: React.FormEvent) {
    submitEvent.preventDefault();
    if (busy || trimmed.length === 0) return;
    setBusy(true);
    setError(null);

    try {
      const { question } = await apiRequest<{ question: OwnAskedQuestion }>(
        `/public/join/${code}/questions`,
        { method: 'POST', anonymous: true, body: { deviceToken, content: trimmed } },
      );
      setDraft('');
      onAsked(question);
    } catch (caught) {
      // The server's own message, because each refusal here is a different
      // thing the asker can act on: wait a moment, shorten it, or the room
      // stopped taking questions.
      setError(caught instanceof ApiError ? caught.message : 'Could not send your question.');
    } finally {
      setBusy(false);
    }
  }

  async function like(askedQuestionId: string) {
    if (liked.has(askedQuestionId)) return;
    // Marked before the request, not after: a double tap on a phone is one
    // gesture, and the second would otherwise spend a request earning a 409.
    setLiked((current) => new Set(current).add(askedQuestionId));

    try {
      const result = await apiRequest<{ likes: number }>(
        `/public/join/${code}/questions/${askedQuestionId}/like`,
        { method: 'POST', anonymous: true, body: { deviceToken } },
      );
      onLiked(askedQuestionId, result.likes);
    } catch (caught) {
      // A like is not worth interrupting the room for. `already_liked` means
      // the optimistic mark above was right; anything else leaves the count
      // to be corrected by the next push or resync.
      if (!(caught instanceof ApiError) || caught.code !== 'conflict') {
        setLiked((current) => {
          const next = new Set(current);
          next.delete(askedQuestionId);
          return next;
        });
      }
    }
  }

  return (
    <section className="taker-qanda">
      <h2>Ask the presenter</h2>

      <form onSubmit={ask}>
        <textarea
          className="taker-qanda-input"
          value={draft}
          onChange={(changeEvent) => setDraft(changeEvent.target.value)}
          maxLength={MAX_ASKED_QUESTION_LENGTH}
          rows={3}
          placeholder="What would you like to ask?"
          aria-label="Your question"
          disabled={busy}
        />
        <div className="taker-qanda-actions">
          {/* Only once it is close enough to matter — a counter sitting at 500
              from the first keystroke is noise. */}
          <span className="taker-qanda-remaining" aria-hidden={remaining > 50}>
            {remaining <= 50 ? `${remaining} left` : ''}
          </span>
          <button type="submit" disabled={busy || trimmed.length === 0}>
            {busy ? 'Sending…' : 'Ask'}
          </button>
        </div>
      </form>

      {error ? (
        <p className="taker-qanda-error" role="alert">
          {error}
        </p>
      ) : null}

      {myQuestions.length > 0 ? (
        <div className="taker-qanda-mine">
          <h3>Your questions</h3>
          <ul>
            {myQuestions.map((question) => (
              <li key={question.askedQuestionId} className={`taker-qanda-mine-${question.status}`}>
                <p className="taker-qanda-text">{question.content}</p>
                <p className="taker-qanda-status">{STATUS_LABEL[question.status]}</p>
                {/* The moderator's note, and the only surface it ever reaches.
                    Plain text: it is typed by one stranger and read by another. */}
                {question.rejectionReason ? (
                  <p className="taker-qanda-reason">{question.rejectionReason}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="taker-qanda-feed">
        <h3>From the room</h3>
        {questions.length === 0 ? (
          <p className="muted">No questions yet. Yours could be the first.</p>
        ) : (
          <ul>
            {questions.map((question) => {
              const isPresented = question.askedQuestionId === presentedAskedQuestionId;
              const hasLiked = liked.has(question.askedQuestionId);
              return (
                <li
                  key={question.askedQuestionId}
                  className={`taker-qanda-item${isPresented ? ' presented' : ''}`}
                >
                  <p className="taker-qanda-text">{question.content}</p>
                  {isPresented ? <p className="taker-qanda-now">On screen now</p> : null}
                  {question.answer ? <p className="taker-qanda-answer">{question.answer}</p> : null}
                  <button
                    type="button"
                    className="taker-qanda-like"
                    onClick={() => void like(question.askedQuestionId)}
                    disabled={hasLiked}
                    aria-label={
                      hasLiked
                        ? `Liked — ${question.likes} ${question.likes === 1 ? 'like' : 'likes'}`
                        : `Like this question — ${question.likes} so far`
                    }
                  >
                    <span aria-hidden="true">▲</span> {question.likes}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
